import { ChakraProvider, Box, Spinner, Center, Text } from '@chakra-ui/react';
import { BrowserRouter as Router, Routes, Route, useParams } from 'react-router-dom';
import { PodcastList } from './components/PodcastList';
import { PodcastDetail } from './components/PodcastDetail';
import { PodcastInsights } from './components/PodcastInsights';
import { AudioProvider } from './contexts/AudioContext';
import { MainAudioPlayer } from './components/MainAudioPlayer';
import { Header } from './components/Header';
import { Login } from './components/Login';
import { ProtectedRoute } from './components/ProtectedRoute';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { GOOGLE_CLIENT_ID } from './utils/authConfig';
import './styles/markdown.css';
import { useState, useEffect } from 'react';
import { Episode } from './utils/episodes';
import { PodcastData } from './types/podcast';
import theme from './styles/theme';
import { getCachedEpisodeDetails, cacheEpisodeDetails, prewarmCache } from './utils/cache';
import ForYou from './components/ForYou';
import { API_BASE_URL } from './config/api';

function App() {
  // Pre-warm cache when app starts
  useEffect(() => {
    prewarmCache();
  }, []);

  const AppContent = () => (
    <ChakraProvider theme={theme}>
      <AudioProvider>
        <Box minH="100vh" bg="white">
          <Header />
          <Box pt="72px">
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <ForYou shouldRedirectOnEmpty={true} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/for-you"
                element={
                  <ProtectedRoute>
                    <ForYou shouldRedirectOnEmpty={false} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/discover"
                element={
                  <ProtectedRoute>
                    <PodcastList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/podcasts/:guid"
                element={
                  <ProtectedRoute>
                    <PodcastDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/episodes/:id"
                element={
                  <ProtectedRoute>
                    <EpisodePage />
                  </ProtectedRoute>
                }
              />
            </Routes>
            <MainAudioPlayer />
          </Box>
        </Box>
      </AudioProvider>
    </ChakraProvider>
  );

  return (
    <Router>
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
        <AppContent />
      </GoogleOAuthProvider>
    </Router>
  );
}

// Create a new component for the episode page that uses PodcastInsights
const EpisodePage = () => {
  const { id } = useParams<{ id: string }>();
  const [episodeData, setEpisodeData] = useState<PodcastData | null>(null);
  const [episode, setEpisode] = useState<Episode | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [podcastImage, setPodcastImage] = useState<string | undefined>(undefined);

  useEffect(() => {
    const fetchEpisodeData = async () => {
      if (!id) {
        setError('No episode ID provided');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        // Try to get cached data first
        const cachedData = getCachedEpisodeDetails(id);
        if (cachedData) {
          setEpisodeData(cachedData);
          if (cachedData.episode_metadata) {
            const newEpisode: Episode = {
              episode_id: id,
              episode_name: cachedData.episode_metadata.episode_name || 'Untitled Episode',
              podcast_description: cachedData.episode_metadata.description || '',
              published_date: cachedData.episode_metadata.published_date || new Date().toISOString(),
              podcast_name: cachedData.episode_metadata.podcast_name || 'Unknown Podcast',
              podcast_guid: cachedData.episode_metadata.podcast_guid || '',
              s3_url: cachedData.episode_metadata.s3_url || '',
              podcast_image: cachedData.podcast_image,
              description: cachedData.episode_metadata.description || ''
            };
            setEpisode(newEpisode);
            
            if (cachedData.episode_metadata.podcast_guid) {
              await fetchPodcastImage(cachedData.episode_metadata.podcast_guid);
            }
          }
          setIsLoading(false);
          return;
        }

        // If no cached data, fetch from API
        const response = await fetch(`${API_BASE_URL}/api/episode/${encodeURIComponent(id)}`);
        
        if (!response.ok) {
          throw new Error(
            `Failed to fetch episode: ${response.status} ${response.statusText}`
          );
        }

        const data = await response.json();
        const metadata = data as PodcastData;
        setEpisodeData(metadata);

        if (metadata.episode_metadata) {
          const newEpisode: Episode = {
            episode_id: id,
            episode_name: metadata.episode_metadata.episode_name || 'Untitled Episode',
            podcast_description: metadata.episode_metadata.description || '',
            published_date: metadata.episode_metadata.published_date || new Date().toISOString(),
            podcast_name: metadata.episode_metadata.podcast_name || 'Unknown Podcast',
            podcast_guid: metadata.episode_metadata.podcast_guid || '',
            s3_url: metadata.episode_metadata.s3_url || '',
            podcast_image: data.podcast_image,
            description: metadata.episode_metadata.description || ''
          };
          setEpisode(newEpisode);
          
          setPodcastImage(data.podcast_image);
          
          if (!data.podcast_image && metadata.episode_metadata.podcast_guid) {
            await fetchPodcastImage(metadata.episode_metadata.podcast_guid);
          }
        }

        // Cache the episode data
        cacheEpisodeDetails(id, metadata);
      } catch (err) {
        console.error('Error in fetchEpisodeData:', err);
        setError(
          err instanceof Error 
            ? err.message 
            : 'An unexpected error occurred while loading the episode'
        );
      } finally {
        setIsLoading(false);
      }
    };

    const fetchPodcastImage = async (podcastGuid: string) => {
      try {
        const podcastResponse = await fetch(`${API_BASE_URL}/api/podcast/${podcastGuid}`);
        if (!podcastResponse.ok) {
          throw new Error(`Failed to fetch podcast: ${podcastResponse.status}`);
        }
        const podcastData = await podcastResponse.json();
        
        // Get the image URL and ensure it's properly formatted
        let imageUrl = podcastData.podcast?.image_url;
        
        // If we have an image URL, make sure it's properly formatted
        if (imageUrl) {
          // Clean the URL if needed
          imageUrl = imageUrl.trim();
          
          // Log the image URL for debugging
          console.log('Podcast image URL:', imageUrl);
          
          // Set the podcast image
          setPodcastImage(imageUrl);
        } else {
          // If no image URL is available, use a default logo
          setPodcastImage('/images/podsight.png');
        }
      } catch (err) {
        console.error('Failed to fetch podcast image:', err);
        // Use default image on error
        setPodcastImage('/images/podsight.png');
      }
    };

    fetchEpisodeData();
  }, [id]);

  if (isLoading) {
    return (
      <Center py={8}>
        <Spinner size="xl" />
      </Center>
    );
  }

  if (error || !episodeData || !episode) {
    return (
      <Center py={8}>
        <Text color="red.500" fontSize="lg" fontWeight="medium">
          {error || 'Episode not found'}
        </Text>
      </Center>
    );
  }

  return (
    <PodcastInsights
      data={episodeData}
      audioUrl={`${API_BASE_URL}/api/audio/${encodeURIComponent(episode.episode_id)}`}
      episodeTitle={episode.episode_name}
      podcastName={episode.podcast_name}
      podcastImage={podcastImage}
      publishedDate={episode.published_date}
    />
  );
};

export default App; 